import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXl } from "@/domains/design/components/body-xl"
import { MinusIcon } from "@/domains/icons/huge-icons/monotone/minus"
import { PlusIcon } from "@/domains/icons/huge-icons/monotone/plus"

import { formatPriceDifference } from "../../utils/price-to-tick-conversion"

export interface PriceRangeInputViewProps {
  title: string
  price: string | undefined // From priceRangeCalculation.pricesAtTicks[0] or [1]
  priceDifference: number | undefined // From priceRangeCalculation.priceDifferences.min or .max
  onIncrement: () => void // Calls store.incrementMinPrice() or incrementMaxPrice()
  onDecrement: () => void // Calls store.decrementMinPrice() or decrementMaxPrice()
  isIncrementDisabled?: boolean // When at MAX_TICK
  isDecrementDisabled?: boolean // When at MIN_TICK
}

/**
 * Price Range Input View
 * Pure View component that displays price value, percentage difference, and adjustment buttons
 * Follows DDD pattern: receives all data as props, no store access
 */
export function PriceRangeInputView({
  title,
  price,
  priceDifference,
  onIncrement,
  onDecrement,
  isIncrementDisabled = false,
  isDecrementDisabled = false,
}: PriceRangeInputViewProps) {
  // Format price with 8 decimal places for precision
  const formattedPrice =
    price === "∞"
      ? "∞"
      : price
        ? parseFloat(price).toLocaleString(undefined, {
            minimumFractionDigits: 8,
            maximumFractionDigits: 8,
          })
        : ""

  // Format percentage difference
  const formattedDifference = formatPriceDifference(priceDifference)

  // Determine if buttons should be disabled
  const hasPrice = price !== undefined && price !== ""
  const incrementDisabled = !hasPrice || isIncrementDisabled
  const decrementDisabled = !hasPrice || isDecrementDisabled

  return (
    <div className="flex flex-1 flex-col gap-2 rounded-lg border border-p400-light bg-p400 p-4">
      {/* Title */}
      <BodySM As="p" className="text-t200">
        {title}
      </BodySM>

      {/* Price and Controls Row */}
      <div className="flex items-start justify-between gap-4">
        {/* Price and Percentage */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Price Value */}
          <BodyXl As="p" className="font-black tracking-tight text-t100">
            {formattedPrice || "-"}
          </BodyXl>

          {/* Percentage Difference */}
          {formattedDifference && (
            <BodySM As="p" className="text-t200">
              {formattedDifference}
            </BodySM>
          )}
        </div>

        {/* Plus/Minus Buttons */}
        <div className="flex flex-col gap-1">
          {/* Plus Button */}
          <button
            type="button"
            onClick={() => {
              onIncrement()
            }}
            disabled={incrementDisabled}
            className={`
              flex h-8 w-8 items-center justify-center rounded-full border transition-colors
              ${
                incrementDisabled
                  ? "cursor-not-allowed border-p300-light bg-p300 text-t300"
                  : "border-p200-light bg-p200 text-t100 hover:bg-p200-light active:bg-p200-shade"
              }
            `}
            aria-label={`Increase ${title}`}
          >
            <PlusIcon className="h-4 w-4" />
          </button>

          {/* Minus Button */}
          <button
            type="button"
            onClick={() => {
              onDecrement()
            }}
            disabled={decrementDisabled}
            className={`
              flex h-8 w-8 items-center justify-center rounded-full border transition-colors
              ${
                decrementDisabled
                  ? "cursor-not-allowed border-p300-light bg-p300 text-t300"
                  : "border-p200-light bg-p200 text-t100 hover:bg-p200-light active:bg-p200-shade"
              }
            `}
            aria-label={`Decrease ${title}`}
          >
            <MinusIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
